import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { validateForm, ValidationErrors } from './formValidation';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
      <i className="ri-error-warning-line"></i>
      {message}
    </p>
  );
}

export default function SellerForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    propertyCondition: '',
    timeline: '',
    additionalInfo: ''
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Re-validate the changed field on the fly if already touched
    if (touched[name]) {
      const newData = { ...formData, [name]: value };
      const newErrors = validateForm(newData);
      setErrors(prev => ({ ...prev, [name]: newErrors[name as keyof ValidationErrors] }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const newErrors = validateForm({ ...formData });
    setErrors(prev => ({ ...prev, [name]: newErrors[name as keyof ValidationErrors] }));
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm transition-colors ${
      touched[field] && errors[field as keyof ValidationErrors]
        ? 'border-red-400 bg-red-50'
        : 'border-gray-300'
    }`;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(false);

    // Mark all validatable fields as touched
    setTouched({ name: true, email: true, phone: true, address: true, zipCode: true, state: true, propertyCondition: true, timeline: true });

    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    const extraErrors: ValidationErrors = { ...validationErrors };
    if (!formData.propertyCondition) extraErrors.propertyCondition = 'Please select the property condition.';
    if (!formData.timeline) extraErrors.timeline = 'Please select when you need to sell.';
    setErrors(extraErrors);

    if (Object.keys(extraErrors).length > 0) return;

    if (formData.additionalInfo.length > 500) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('seller_leads').insert({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(),
        zip_code: formData.zipCode.trim(),
        property_condition: formData.propertyCondition,
        timeline: formData.timeline,
        additional_info: formData.additionalInfo,
      });

      if (error) {
        setSubmitError(true);
      } else {
        navigate('/thank-you');
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="seller-form" className="py-24 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Your Free <span className="text-emerald-600">Cash Offer</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Fill out the form below and receive a no-obligation cash offer for your property within 24 hours. All information is kept confidential.
          </p>
        </div>

        <div className="bg-white rounded-lg p-8 md:p-10">
          <form id="seller-inquiry-form" data-readdy-form onSubmit={handleSubmit} noValidate>

            {/* Personal Information */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-user-line text-emerald-600"></i>
                Your Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={inputClass('name')}
                    placeholder="John Smith"
                    autoComplete="name"
                  />
                  <FieldError message={touched.name ? errors.name : undefined} />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={inputClass('email')}
                    placeholder="john@example.com"
                    autoComplete="email"
                  />
                  <FieldError message={touched.email ? errors.email : undefined} />
                </div>
              </div>
              <div className="mt-6">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={inputClass('phone')}
                  placeholder="(202) 600-9590"
                  autoComplete="tel"
                />
                <FieldError message={touched.phone ? errors.phone : undefined} />
              </div>
            </div>

            {/* Property Information */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-home-4-line text-emerald-600"></i>
                Property Details
              </h3>
              <div className="mb-6">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Property Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={inputClass('address')}
                  placeholder="123 Main Street"
                  autoComplete="street-address"
                />
                <FieldError message={touched.address ? errors.address : undefined} />
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    placeholder="City"
                    autoComplete="address-level2"
                  />
                </div>
                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    placeholder="e.g. TX or Texas"
                    autoComplete="address-level1"
                    maxLength={20}
                  />
                </div>
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={inputClass('zipCode')}
                    placeholder="12345"
                    autoComplete="postal-code"
                    maxLength={5}
                  />
                  <FieldError message={touched.zipCode ? errors.zipCode : undefined} />
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-emerald-600"></i>
                Additional Details
              </h3>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="propertyCondition" className="block text-sm font-medium text-gray-700 mb-2">
                    Property Condition <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="propertyCondition"
                    name="propertyCondition"
                    value={formData.propertyCondition}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`${inputClass('propertyCondition')} cursor-pointer`}
                  >
                    <option value="">Select condition</option>
                    <option value="excellent">Excellent - Move-in ready</option>
                    <option value="good">Good - Minor repairs needed</option>
                    <option value="fair">Fair - Some repairs needed</option>
                    <option value="poor">Poor - Major repairs needed</option>
                  </select>
                  <FieldError message={touched.propertyCondition ? errors.propertyCondition : undefined} />
                </div>
                <div>
                  <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                    When do you need to sell? <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`${inputClass('timeline')} cursor-pointer`}
                  >
                    <option value="">Select timeline</option>
                    <option value="asap">As soon as possible</option>
                    <option value="1-month">Within 1 month</option>
                    <option value="3-months">Within 3 months</option>
                    <option value="flexible">Flexible timeline</option>
                  </select>
                  <FieldError message={touched.timeline ? errors.timeline : undefined} />
                </div>
              </div>
              <div>
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Information (Optional)
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us more about your situation or any special circumstances..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.additionalInfo.length}/500 characters</p>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-emerald-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-emerald-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Submitting...
                </span>
              ) : 'Get My Free Cash Offer'}
            </button>

            {/* Submission error */}
            {submitError && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-center font-medium text-sm">
                  <i className="ri-error-warning-line text-lg mr-2"></i>
                  Something went wrong on our end. Please try again or call us at 202-600-9590.
                </p>
              </div>
            )}

            {/* Privacy Notice */}
            <p className="text-xs text-gray-500 text-center mt-6">
              By submitting this form, you provide express written consent for GreenState Investments to contact you via call, text, or email. By submitting this form, you agree to our Legal Terms &amp; Privacy Policy.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
